import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import CalendlyWidget from 'components/CalendlyWidget';

const TechnologyStack = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [hoveredTech, setHoveredTech] = useState(null);

  const techCategories = [
    {
      id: 1,
      name: "Frontend",
      icon: "Monitor",
      color: "accent",
      description: "Modern frontend technologies for exceptional user experiences",
      technologies: [
        {
          name: "React",
          description: "A JavaScript library for building user interfaces with component-based architecture",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
          experience: "5+ years",
          projects: "200+",
          expertise: "Expert"
        },
        {
          name: "Next.js",
          description: "The React framework for production with server-side rendering and static generation",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg",
          experience: "3+ years",
          projects: "150+",
          expertise: "Advanced"
        },
        {
          name: "Vue.js",
          description: "Progressive JavaScript framework for building modern web interfaces",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg",
          experience: "4+ years",
          projects: "120+",
          expertise: "Advanced"
        },
        {
          name: "TypeScript",
          description: "Typed superset of JavaScript that compiles to plain JavaScript",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg",
          experience: "4+ years",
          projects: "180+",
          expertise: "Expert"
        },
        {
          name: "Tailwind CSS",
          description: "Utility-first CSS framework for rapidly building custom designs",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-plain.svg",
          experience: "3+ years",
          projects: "160+",
          expertise: "Expert"
        },
        {
          name: "Three.js",
          description: "JavaScript 3D library for creating and displaying animated 3D computer graphics",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/threejs/threejs-original.svg",
          experience: "2+ years",
          projects: "50+",
          expertise: "Intermediate"
        }
      ]
    },
    {
      id: 2,
      name: "Backend",
      icon: "Server",
      color: "success",
      description: "Robust backend solutions for scalable applications",
      technologies: [
        {
          name: "Node.js",
          description: "JavaScript runtime built on Chrome\'s V8 JavaScript engine for server-side development",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
          experience: "5+ years",
          projects: "180+",
          expertise: "Expert"
        },
        {
          name: "Python",
          description: "High-level programming language for web development, AI, and data science",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
          experience: "6+ years",
          projects: "150+",
          expertise: "Expert"
        },
        {
          name: "Express.js",
          description: "Fast, unopinionated, minimalist web framework for Node.js",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg",
          experience: "5+ years",
          projects: "160+",
          expertise: "Expert"
        },
        {
          name: "Django",
          description: "High-level Python web framework that encourages rapid development",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/django/django-plain.svg",
          experience: "4+ years",
          projects: "100+",
          expertise: "Advanced"
        },
        {
          name: "GraphQL",
          description: "Query language for APIs and runtime for executing those queries",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg",
          experience: "3+ years",
          projects: "80+",
          expertise: "Advanced"
        },
        {
          name: "PostgreSQL",
          description: "Advanced open source relational database with strong SQL compliance",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
          experience: "5+ years",
          projects: "140+",
          expertise: "Expert"
        }
      ]
    },
    {
      id: 3,
      name: "Mobile",
      icon: "Smartphone",
      color: "warning",
      description: "Cross-platform and native mobile development solutions",
      technologies: [
        {
          name: "React Native",
          description: "Framework for building native mobile apps using React and JavaScript",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
          experience: "4+ years",
          projects: "90+",
          expertise: "Advanced"
        },
        {
          name: "Flutter",
          description: "Google\'s UI toolkit for building natively compiled applications",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg",
          experience: "3+ years",
          projects: "70+",
          expertise: "Advanced"
        },
        {
          name: "Swift",
          description: "Powerful programming language for iOS, macOS, watchOS, and tvOS",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/swift/swift-original.svg",
          experience: "4+ years",
          projects: "60+",
          expertise: "Advanced"
        },
        {
          name: "Kotlin",
          description: "Modern programming language for Android development and beyond",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg",
          experience: "3+ years",
          projects: "55+",
          expertise: "Intermediate"
        },
        {
          name: "Firebase",
          description: "Google\'s mobile and web application development platform",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg",
          experience: "4+ years",
          projects: "100+",
          expertise: "Advanced"
        },
        {
          name: "Expo",
          description: "Platform for making universal native apps for Android, iOS, and the web",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/expo/expo-original.svg",
          experience: "3+ years",
          projects: "80+",
          expertise: "Advanced"
        }
      ]
    },
    {
      id: 4,
      name: "Cloud & DevOps",
      icon: "Cloud",
      color: "purple-500",
      description: "Modern cloud infrastructure and deployment solutions",
      technologies: [
        {
          name: "AWS",
          description: "Amazon Web Services cloud computing platform with comprehensive services",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original.svg",
          experience: "5+ years",
          projects: "120+",
          expertise: "Expert"
        },
        {
          name: "Docker",
          description: "Platform for developing, shipping, and running applications in containers",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
          experience: "4+ years",
          projects: "100+",
          expertise: "Advanced"
        },
        {
          name: "Kubernetes",
          description: "Open-source container orchestration platform for automating deployment",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
          experience: "3+ years",
          projects: "60+",
          expertise: "Advanced"
        },
        {
          name: "Vercel",
          description: "Cloud platform for static sites and serverless functions deployment",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vercel/vercel-original.svg",
          experience: "3+ years",
          projects: "150+",
          expertise: "Advanced"
        },
        {
          name: "GitHub Actions",
          description: "CI/CD platform that allows automation of build, test, and deployment pipeline",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg",
          experience: "4+ years",
          projects: "140+",
          expertise: "Advanced"
        },
        {
          name: "Terraform",
          description: "Infrastructure as code tool for building, changing, and versioning infrastructure",
          logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg",
          experience: "2+ years",
          projects: "40+",
          expertise: "Intermediate"
        }
      ]
    }
  ];

  const currentCategory = techCategories?.[activeCategory];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCategory((prev) => (prev + 1) % techCategories?.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [techCategories?.length]);

  return (
    <section className="py-20 bg-gradient-to-b from-background to-primary/5">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Layers" size={16} />
            <span>Technology Ecosystem</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Cutting-Edge
            <span className="block bg-gradient-to-r from-accent to-success bg-clip-text text-transparent">
              Technology Stack
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We leverage the latest technologies and frameworks to build scalable, 
            performant, and future-ready solutions for your business.
          </p>
        </motion.div>

        {/* Category Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {techCategories?.map((category, index) => (
            <motion.button
              key={category?.id}
              onClick={() => setActiveCategory(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center space-x-3 px-6 py-3 rounded-xl transition-all duration-300 ${
                index === activeCategory
                  ? `bg-${category?.color}/20 text-${category?.color} border border-${category?.color}/30 shadow-lg`
                  : 'bg-card text-muted-foreground hover:bg-muted/50 border border-border'
              }`}
            >
              <Icon name={category?.icon} size={20} />
              <span className="font-medium">{category?.name}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Technology Grid */}
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* Category Description */}
          <div className="text-center">
            <h3 className={`text-2xl font-bold text-${currentCategory?.color} mb-2`}>
              {currentCategory?.name} Technologies
            </h3>
            <p className="text-muted-foreground">
              {currentCategory?.description}
            </p>
          </div>

          {/* Technologies Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentCategory?.technologies?.map((tech, index) => (
              <motion.div
                key={tech?.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setHoveredTech(tech?.name)}
                onMouseLeave={() => setHoveredTech(null)}
                className={`relative bg-card border border-border rounded-xl p-6 hover:border-${currentCategory?.color}/30 transition-all duration-300 group cursor-pointer`}
              >
                {/* Technology Logo */}
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                    <img
                      src={tech?.logo}
                      alt={`${tech?.name} logo`}
                      className="w-8 h-8 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-8 h-8 bg-accent/20 rounded flex items-center justify-center">
                      <Icon name="Code" size={16} className="text-accent" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">{tech?.name}</h4>
                    <span className={`text-sm text-${currentCategory?.color}`}>
                      {tech?.expertise}
                    </span>
                  </div>
                </div>

                {/* Technology Description */}
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {tech?.description}
                </p>

                {/* Technology Stats */}
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-foreground">{tech?.experience}</div>
                    <div className="text-xs text-muted-foreground">Experience</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-foreground">{tech?.projects}</div>
                    <div className="text-xs text-muted-foreground">Projects</div>
                  </div>
                </div>

                {/* Hover Effect */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: hoveredTech === tech?.name ? 1 : 0,
                    scale: hoveredTech === tech?.name ? 1 : 0.8
                  }}
                  className={`absolute inset-0 bg-gradient-to-br from-${currentCategory?.color}/10 to-${currentCategory?.color}/5 rounded-xl border border-${currentCategory?.color}/20 flex items-center justify-center`}
                >
                  <div className="text-center">
                    <Icon name="ExternalLink" size={24} className={`text-${currentCategory?.color} mb-2`} />
                    <span className={`text-sm font-medium text-${currentCategory?.color}`}>
                      View Projects
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Technology Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-accent/10 via-success/10 to-warning/10 border border-accent/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Technology Consultation Available
            </h3>
            <p className="text-muted-foreground mb-6">
              Not sure which technology stack is right for your project? Our experts can help you 
              choose the perfect combination of technologies for your specific requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                // className="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-medium shadow-accent hover:bg-accent/90 transition-colors flex items-center justify-center space-x-2"
              >
{/* inserting calendly */}
 <CalendlyWidget
                      buttonText="Get Tech Consultation"
                      buttonClassName="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-medium shadow-accent hover:bg-accent/90 transition-colors flex items-center justify-center space-x-2"/>

                {/* <Icon name="MessageCircle" size={20} />
                <span>Get Tech Consultation</span> */}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border border-accent/30 text-accent px-6 py-3 rounded-lg font-medium hover:bg-accent/10 transition-colors flex items-center justify-center space-x-2"
              >
                <Icon name="FileText" size={20} />
                <span>Technology Guide</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechnologyStack;